import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Salary.css';

export default function Salary({ employee }) {
  const [salary, setSalary]   = useState(null);
  const [loading, setLoading] = useState(true);

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  useEffect(() => { loadSalary(); }, []);

  const loadSalary = async () => {
    try {
      setLoading(true);
      const data = await api.getMySalary(month, year);
      setSalary(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const POLICY = [
    { category: 'Minor Late',          threshold: '10–30 minutes', deduction: 'Casual leave' },
    { category: 'Major Late',          threshold: '30–60 minutes', deduction: 'Casual leave' },
    { category: 'Every 3 Lates',       threshold: 'In one month',  deduction: '½ day salary/leave' },
    { category: 'Every 6 Lates',       threshold: 'In one month',  deduction: '1 day salary/leave' },
    { category: 'Unauthorized Absent', threshold: 'No clock-in',   deduction: '1 day salary' },
    { category: 'Auto Casual Leave',   threshold: 'No clock-in',   deduction: 'Casual leave used' },
    { category: 'Grace Period',        threshold: '—',             deduction: '20 minutes' },
  ];

  const deductionPct = parseFloat(salary?.deduction_percent || 0);
  const isHealthy     = deductionPct === 0;

  return (
    <div className="salary-page">

      <div className="sal-section-label">
        {now.toLocaleString('default', { month: 'long' })} {year} — Salary Impact
      </div>

      {loading ? (
        <div className="sal-loading"><div className="sal-spinner" /></div>
      ) : salary?.error ? (
        <div className="sal-error-box">{salary.error}</div>
      ) : (
        <>
          {/* Hero deduction banner — gradient, health-aware coloring */}
          <div className={`net-banner-v2 ${isHealthy ? 'net-banner-healthy' : 'net-banner-warning'}`}>
            <div className="net-banner-icon">{isHealthy ? '✓' : '⚠'}</div>
            <div className="net-label">Total Deduction This Month</div>
            <div className="net-amount">{salary?.deduction_percent || '0.00'}%</div>
            <div className="net-sub">
              {salary?.has_salary
                ? `of your monthly salary — PKR ${salary.total_deduction || '0.00'} deducted`
                : 'of your monthly salary (salary not yet set up by HR)'}
            </div>
          </div>

          {/* Stat cards — premium gradient */}
          <div className="salary-stats-v2">
            <div className="sal-stat-v2 sal-stat-amber">
              <div className="sal-stat-v2-num">{salary?.absent_days || 0}</div>
              <div className="sal-stat-v2-lbl">Unauthorized Absent</div>
              <div className="sal-stat-v2-sub">salary deducted</div>
            </div>
            <div className="sal-stat-v2 sal-stat-red">
              <div className="sal-stat-v2-num">{salary?.late_count || 0}</div>
              <div className="sal-stat-v2-lbl">Late Count</div>
              <div className="sal-stat-v2-sub">this month</div>
            </div>
          </div>

          <div className="sal-section-label">Breakdown</div>
          <div className="sal-card">
            <div className="sal-row-v2">
              <span className="sal-row-icon">🚫</span>
              <span className="sal-label">Unauthorized Absent</span>
              <span className="sal-val deduct">
                {salary?.absent_days > 0 ? `${salary.absent_days} day(s) deducted` : 'None'}
              </span>
            </div>
            <div className="sal-row-v2">
              <span className="sal-row-icon">⏱️</span>
              <span className="sal-label">Late Deduction</span>
              <span className="sal-val deduct">
                {salary?.late_count > 0 ? `${salary.late_count} late(s)` : 'None'}
              </span>
            </div>
            <div className="sal-row-v2">
              <span className="sal-row-icon">📋</span>
              <span className="sal-label">Casual Leaves (Late Policy)</span>
              <span className="sal-val deduct">
                {salary?.casual_leaves_consumed > 0 ? `${salary.casual_leaves_consumed} day(s)` : 'None'}
              </span>
            </div>
            {salary?.casual_auto_days > 0 && (
              <div className="sal-row-v2">
                <span className="sal-row-icon">⚠️</span>
                <span className="sal-label">Auto Casual Leave (Absent)</span>
                <span className="sal-val" style={{ color: 'var(--amber)' }}>
                  {salary.casual_auto_days} day(s) auto-used
                </span>
              </div>
            )}
            <div className="sal-row-v2">
              <span className="sal-row-icon">✅</span>
              <span className="sal-label">Approved Leaves</span>
              <span className="sal-val" style={{ color: 'var(--green)' }}>
                {salary?.approved_leaves || 0} day(s)
              </span>
            </div>
            {salary?.public_holidays > 0 && (
              <div className="sal-row-v2">
                <span className="sal-row-icon">🎉</span>
                <span className="sal-label">Public Holidays</span>
                <span className="sal-val" style={{ color: 'var(--blue)' }}>
                  {salary.public_holidays} day(s) — no deduction
                </span>
              </div>
            )}
            <div className="sal-row net">
              <span>Total Deduction</span>
              <span className="sal-val deduct">{salary?.deduction_percent || '0.00'}% of salary</span>
            </div>
          </div>

          <div className="sal-section-label" style={{ marginTop: 16 }}>Policy Reference</div>
          <div className="sal-card sal-policy-card">
            <div className="policy-header">
              <span>Rule</span>
              <span>When</span>
              <span>Penalty</span>
            </div>
            {POLICY.map((p, i) => (
              <div key={i} className="policy-row">
                <span className="policy-cat">{p.category}</span>
                <span className="policy-thresh">{p.threshold}</span>
                <span className="policy-ded">{p.deduction}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}